import React, { useState, useEffect } from "react";
import { StyleSheet, Text, View } from "react-native";
import { LinkContainer } from "react-router-bootstrap";
import LoaderButton from "./LoaderButton";
import FormTextInput from "./FormTextInput";
import FormGroup from "./FormGroup";
import { useNavigate } from "react-router-dom";

export type StateProps = {
  loading: boolean;
  confirming: boolean;
  isAuthenticated: boolean;
  errorMessage: string | null | undefined;
};
export type DispatchProps = {
  signup: (arg0: string, arg1: string) => void;
  confirmSignup: (arg0: string, arg1: string, arg2: string) => void;
};
export type OwnProps = {};
export type Props = StateProps & DispatchProps & OwnProps;
type PasswordValidation = {
  passwordLength: boolean;
  passwordMatch: boolean;
  passwordUppercase: boolean;
  passwordLowercase: boolean;
  passwordSpecial: boolean;
  passwordNumber: boolean;
};
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const UPPERCASE_REGEX = /[A-Z]/g;
const LOWERCASE_REGEX = /[a-z]/g;
const NUMBER_REGEX = /[0-9]/g;

function validateEmail(email: string): {
  email: boolean;
} {
  return {
    email: EMAIL_REGEX.test(email),
  };
}

function validatePasswords(
  password: string,
  confirmPassword: string
): PasswordValidation {
  const cleanedPassword = password
    .replace(NUMBER_REGEX, "")
    .replace(LOWERCASE_REGEX, "")
    .replace(UPPERCASE_REGEX, "");
  return {
    passwordMatch: password.length > 0 && password === confirmPassword,
    passwordLength: password.length >= 8,
    passwordUppercase: UPPERCASE_REGEX.test(password),
    passwordLowercase: LOWERCASE_REGEX.test(password),
    passwordNumber: NUMBER_REGEX.test(password),
    passwordSpecial: cleanedPassword.length > 0,
  };
}

export default function Signup(props: Props) {
  const nav = useNavigate();
  const { isAuthenticated } = props;
  useEffect(() => {
    if (isAuthenticated) {
      nav("/");
    }
  }, [isAuthenticated, nav]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");

  function validateForm() {
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePasswords(password, confirmPassword);
    return (
      emailValidation.email &&
      passwordValidation.passwordLength &&
      passwordValidation.passwordMatch &&
      passwordValidation.passwordUppercase &&
      passwordValidation.passwordLowercase &&
      passwordValidation.passwordSpecial &&
      passwordValidation.passwordNumber
    );
  }

  function validateConfirmationForm() {
    return confirmationCode.length > 0;
  }

  function onSignupSubmit() {
    props.signup(email, password);
  }

  function onConfirmSubmit() {
    props.confirmSignup(confirmationCode, email, password);
  }

  function renderConfirmationForm() {
    return (
      <View>
        <FormGroup>
          <Text>Confirmation Code</Text>
          <FormTextInput
            onChangeText={setConfirmationCode}
            value={confirmationCode}
          />
          <Text>Please check your email for the code.</Text>
        </FormGroup>
        <FormGroup>
          <LoaderButton
            disabled={!validateConfirmationForm()}
            label="Confirm"
            onPress={onConfirmSubmit}
            loading={props.loading}
          ></LoaderButton>
        </FormGroup>
      </View>
    );
  }

  function renderSummary(validations: Array<boolean>) {
    const text = validations.map((valid) => (valid ? "✓" : "")).join("");
    return <Text style={styles.valid}>{text}</Text>;
  }

  function renderValidationText(text: string, isValid: boolean) {
    return isValid ? null : <Text style={styles.notvalid}>- {text}</Text>;
  }

  function renderEmailFeedback() {
    const valid = validateEmail(email);
    return valid.email ? null : (
      <FormGroup>{renderValidationText("valid email", valid.email)}</FormGroup>
    );
  }

  function renderPasswordFeedback() {
    const valid = validatePasswords(password, confirmPassword);
    return (
      <FormGroup>
        {renderValidationText("8 characters or more", valid.passwordLength)}
        {renderValidationText("passwords match", valid.passwordMatch)}
        {renderValidationText("uppercase characters", valid.passwordUppercase)}
        {renderValidationText("lowercase characters", valid.passwordLowercase)}
        {renderValidationText("special characters", valid.passwordSpecial)}
        {renderValidationText("number characters", valid.passwordNumber)}
      </FormGroup>
    );
  }

  function renderValidationSummary() {
    const valid = {
      ...validatePasswords(password, confirmPassword),
      ...validateEmail(email),
    };
    return (
      <FormGroup>
        {renderSummary([
          valid.passwordLength,
          valid.passwordMatch,
          valid.passwordUppercase,
          valid.passwordLowercase,
          valid.passwordSpecial,
          valid.passwordNumber,
          valid.email,
        ])}
      </FormGroup>
    );
  }

  function renderErrorMessage() {
    return props.errorMessage == null ? null : (
      <FormGroup>
        <Text style={styles.error}>{props.errorMessage}</Text>
      </FormGroup>
    );
  }

  function renderForm() {
    // const errorText =
    //   props.errorMessage == null ? null : (
    //     <FormGroup>
    //       <Text>{props.errorMessage}</Text>
    //     </FormGroup>
    //   );
    return (
      <View>
        <FormGroup>
          <Text>Email</Text>
          <FormTextInput
            autoFocus
            autoCapitalize="none"
            onChangeText={setEmail}
            type="email"
            value={email}
          />
        </FormGroup>
        {renderEmailFeedback()}
        <FormGroup>
          <Text>Password</Text>
          <FormTextInput
            secureTextEntry
            value={password}
            onChangeText={setPassword}
          />
        </FormGroup>
        <FormGroup>
          <Text>Confirm Password</Text>
          <FormTextInput
            secureTextEntry
            onChangeText={setConfirmPassword}
            value={confirmPassword}
          />
        </FormGroup>
        {renderPasswordFeedback()}
        {renderValidationSummary()}
        {renderErrorMessage()}
        <FormGroup>
          <LoaderButton
            loading={props.loading}
            disabled={!validateForm()}
            label="Submit"
            onPress={onSignupSubmit}
          />
        </FormGroup>
        <FormGroup>
          <LinkContainer
            style={{
              marginTop: 30,
              width: "100%",
              textAlign: "center",
              fontWeight: "bold",
            }}
            to="/login"
          >
            <div>Login Instead</div>
          </LinkContainer>
        </FormGroup>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {props.confirming ? renderConfirmationForm() : renderForm()}
    </View>
  );
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    alignSelf: "center",
    maxWidth: 400,
    width: "100%",
  },
  valid: {
    color: "green",
  },
  notvalid: {
    color: "grey",
  },
  error: {
    color: "red",
  },
});
